import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck, AlertCircle } from "lucide-react";
import { SpnLogo } from "@/components/brand/spn-logo";
import { useVerifyMagicLink } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { trackFunnelEvent } from "@/lib/funnel-analytics";

export default function VerifyPage() {
  const [, navigate] = useLocation();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const ranRef = useRef(false);

  const verifyMut = useVerifyMagicLink({
    mutation: {
      onSuccess: () => {
        trackFunnelEvent("first_successful_sign_in", "verify");
        qc.invalidateQueries();
        toast.success("Signed in");
        const next = new URLSearchParams(window.location.search).get("next");
        navigate(next && next.startsWith("/") ? next : "/dashboard");
      },
      onError: (e) => {
        const msg = e?.message ?? "This sign-in link is invalid or has expired.";
        setError(msg);
        toast.error(msg);
      },
    },
  });

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (!token) {
      setError("Missing token in sign-in link.");
      return;
    }
    verifyMut.mutate({ data: { token } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-100 via-white to-blue-50 p-6">
      <div className="pointer-events-none absolute inset-0 [background:radial-gradient(900px_circle_at_85%_-10%,hsl(217_91%_60%/0.18),transparent_60%),radial-gradient(700px_circle_at_-10%_110%,hsl(199_89%_48%/0.18),transparent_60%)]" />
      <Card className="relative w-full max-w-md shadow-xl">
        <CardHeader>
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            {error ? <AlertCircle className="h-5 w-5" /> : <ShieldCheck className="h-5 w-5" />}
          </div>
          <CardTitle className="text-2xl">
            {error ? "Sign-in link problem" : "Signing you in…"}
          </CardTitle>
          <CardDescription>
            {error
              ? "Request a fresh link to continue."
              : "Verifying your magic link with Synergistic Partner Network."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!error && (
            <div className="flex items-center gap-3 rounded-md border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
              <Spinner className="h-4 w-4 text-primary" />
              <span>Hang tight — this only takes a moment.</span>
            </div>
          )}
          {error && (
            <>
              <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                {error}
              </div>
              <Button className="w-full gap-2" onClick={() => navigate("/login")}>
                Back to sign in
                <ArrowRight className="h-4 w-4" />
              </Button>
            </>
          )}
          <div className="flex items-center gap-2 pt-2 text-xs text-muted-foreground">
            <SpnLogo className="h-3.5 w-3.5" />
            Synergistic Partner Network
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
